import React from 'react';
import { useTranslation } from 'react-i18next';

import { Form, FormGenerator } from '@/shared/ui';
import { Dialog } from '@/shared/ui/Dialog';
import { FormFieldValues } from '@/shared/ui/FormGenerator';
import { Button } from '@/shared/ui/lib/Button';

import { changePasswordSchema } from '../model/schemas';
import {
  ChangePasswordDialogProps,
  ChangePasswordFormDataType,
} from '../model/types';
import { useChangePassword } from '../model/useChangePassword';

const CHANGE_PASSWORD_FORM = 'changePasswordForm';
const CHANGE_PASSWORD_FORM_DIALOG = 'changePasswordFormDialog';

export const ChangePasswordDialog = ({
  open,
  onClose,
}: ChangePasswordDialogProps) => {
  const { t } = useTranslation();
  const { changePassword, isPending } = useChangePassword();

  const formScheme = [
    {
      name: 'currentPassword',
      label: t('common:inputs.currentPassword'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.password'),
      },
    },
    {
      name: 'newPassword',
      label: t('common:inputs.newPassword'),
      type: FormFieldValues.Password,
      inputProps: {
        placeholder: t('common:inputs.placeholders.confirmPassword'),
      },
    },
  ];

  const handleCloseDialog = () => {
    onClose?.();
  };

  const handleSubmit = async (data: ChangePasswordFormDataType) => {
    await changePassword(data);
    handleCloseDialog();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      id={CHANGE_PASSWORD_FORM_DIALOG}
      title={t('settings:changePassword.title')}
      leftButtonSlot={
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          disabled={isPending}
        >
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          type="submit"
          form={CHANGE_PASSWORD_FORM}
          variant="contained"
          color="primary"
          disabled={isPending}
        >
          {t('common:buttons.save')}
        </Button>
      }
    >
      <Form
        id={CHANGE_PASSWORD_FORM}
        schema={changePasswordSchema(t)}
        onSubmit={handleSubmit}
        defaultValues={{
          newPassword: '',
          currentPassword: '',
        }}
        mode="onChange"
        sx={{ maxWidth: '400px' }}
      >
        <FormGenerator formScheme={formScheme} />
      </Form>
    </Dialog>
  );
};
